import { z } from 'zod';
import { ProviderKind, ProviderManagedBy, WebhookRegistration } from '@hangar/shared';
import { str, nstr, nint } from './schemaPrimitives';
import { CheckResult, ProviderAccountSummary } from './schemasCommon';
import { Build } from './schemasBuild';

export const ProviderAccountStatus = z.enum(['active', 'degraded', 'disabled']);
export const ProviderAccount = z.object({
  id: str,
  kind: ProviderKind.catch('eas'),
  name: str,
  externalAccount: nstr,
  credentialId: nstr,
  concurrencyLimit: z.number(),
  plan: nstr,
  /** Who owns the provider account; an API that predates the field is read as Malible. */
  managedBy: ProviderManagedBy.catch('malible'),
  inUse: z.number().default(0),
  status: ProviderAccountStatus.catch('active'),
  health: z.object({ checkedAt: nstr, latencyMs: nint, circuit: z.enum(['closed', 'open', 'half_open']).catch('closed').optional(), lastError: nstr }).optional(),
  settings: z.record(str, z.unknown()).default({}),
  createdAt: str,
  etag: str.optional(),
});
export type ProviderAccount = z.infer<typeof ProviderAccount>;
export const ProviderAccountList = z.object({ data: z.array(ProviderAccount), nextCursor: z.string().nullable() });
export const ProviderAccountTestResult = z.object({
  ok: z.boolean(),
  checkedAt: str,
  latencyMs: nint,
  account: z.object({ externalAccount: nstr, displayName: nstr, plan: nstr }).optional(),
  discoveredConcurrency: nint,
  applied: z.boolean().optional(),
  checks: z.array(CheckResult).default([]),
});
export type ProviderAccountTestResult = z.infer<typeof ProviderAccountTestResult>;
/** `POST /provider-accounts/verify`: what an Expo or Bitrise token reaches, before it is stored (operators only). */
export const ProviderTokenVerifyResult = z.object({
  ok: z.boolean(),
  checkedAt: str,
  latencyMs: nint,
  actorType: nstr.default(null),
  accounts: z.array(z.object({ externalAccount: str, displayName: str })).default([]),
  account: z.object({ externalAccount: str, displayName: str, concurrency: nint.default(null) }).nullable().default(null),
  checks: z.array(CheckResult).default([]),
});
export type ProviderTokenVerifyResult = z.infer<typeof ProviderTokenVerifyResult>;

/* ---------- Source control and "Create on Bitrise" ---------- */
export const ScmConnectionStatus = z.enum(['active', 'degraded', 'revoked']);
export const ScmConnection = z.object({
  id: str,
  provider: z.literal('bitbucket').catch('bitbucket'),
  name: str,
  workspace: str,
  /** Set for a repository access token (one repository); null for a workspace token. */
  repository: nstr.default(null),
  credentialId: str,
  status: ScmConnectionStatus.catch('active'),
  health: z.object({ checkedAt: nstr, latencyMs: nint, lastError: nstr }).default({}),
  createdAt: str,
  etag: str.optional(),
});
export type ScmConnection = z.infer<typeof ScmConnection>;
export const ScmConnectionList = z.object({ data: z.array(ScmConnection), nextCursor: z.string().nullable() });
export const ScmConnectionTestResult = z.object({
  ok: z.boolean(),
  checkedAt: str,
  latencyMs: nint,
  account: z.object({ externalAccount: nstr, displayName: nstr }).optional(),
  checks: z.array(CheckResult).default([]),
});
export type ScmConnectionTestResult = z.infer<typeof ScmConnectionTestResult>;
export const ScmRepository = z.object({
  workspace: str,
  slug: str,
  fullName: str,
  name: str,
  isPrivate: z.boolean().default(true),
  defaultBranch: nstr,
  sshCloneUrl: str,
  updatedOn: nstr,
});
export type ScmRepository = z.infer<typeof ScmRepository>;
/** `POST /scm-connections/discover` (plan P34). */
export const ScmTokenDiscoverResult = z.object({
  outcome: z.enum(['listed', 'rejected', 'unsupported']).catch('unsupported'),
  repositories: z.array(ScmRepository).default([]),
  more: z.boolean().default(false),
  message: nstr,
});
export type ScmTokenDiscoverResult = z.infer<typeof ScmTokenDiscoverResult>;
export const ScmRepositoryList = z.object({ scmConnectionId: str, workspace: str, more: z.boolean().default(false), data: z.array(ScmRepository) });
export type ScmRepositoryList = z.infer<typeof ScmRepositoryList>;

export const BitriseSetupStepKey = z.enum(['repository', 'bitrise_app', 'finish', 'config']);
export const BitriseSetupStep = z.object({
  key: BitriseSetupStepKey.catch('repository'),
  label: str,
  status: z.enum(['pending', 'done', 'failed', 'skipped']).catch('pending'),
  at: nstr,
  detail: nstr,
  error: nstr,
  fix: nstr,
});
export type BitriseSetupStep = z.infer<typeof BitriseSetupStep>;
export const BitriseSetup = z.object({
  status: z.enum(['not_started', 'running', 'failed', 'done']).catch('not_started'),
  linked: z.boolean().default(false),
  alreadyProvisioned: z.boolean().optional(),
  scmConnectionId: nstr,
  providerAccountId: nstr,
  repository: z.object({ workspace: str, slug: str, defaultBranch: nstr }).nullish(),
  bitriseAppSlug: nstr,
  bitriseAppUrl: nstr,
  reused: z.boolean().default(false),
  steps: z.array(BitriseSetupStep).default([]),
  configHash: nstr,
  configUploadedAt: nstr,
  configStale: z.boolean().nullish(),
  /** Hangar's outgoing webhook on the Bitrise app; null until a registration was attempted (an older API omits it). */
  webhook: WebhookRegistration.nullish().catch(null),
  workflows: z.array(str).default([]),
  warnings: z.array(str).default([]),
});
export type BitriseSetup = z.infer<typeof BitriseSetup>;
/** A Bitrise app that already builds the repository (`context.existingApps` of the 409). */
export const ExistingBitriseApp = z.object({ slug: str, title: nstr, url: nstr, owner: nstr });
export type ExistingBitriseApp = z.infer<typeof ExistingBitriseApp>;

export const Credential = z.object({ id: str, kind: str, name: str, status: str.optional(), createdAt: str.optional() });

/**
 * `GET /provider-accounts/{id}/bitrise-apps`: the Bitrise apps of the account's workspace, to pick an app's
 * `bitriseAppSlug`. Tolerant on purpose (the route is new): snake_case as Bitrise names the fields or camelCase, a bare
 * array or `{ data }`.
 */
export const BitriseApp = z
  .object({ slug: str, title: nstr, repo_url: nstr, repoUrl: nstr, project_type: nstr, projectType: nstr })
  .transform((a) => ({ slug: a.slug, title: a.title ?? a.slug, repoUrl: a.repo_url ?? a.repoUrl ?? null, projectType: a.project_type ?? a.projectType ?? null }));
export type BitriseApp = z.infer<typeof BitriseApp>;
export const BitriseAppList = z.union([z.array(BitriseApp), z.object({ data: z.array(BitriseApp) }).transform((r) => r.data)]);

export const QueueLane = z.object({
  providerAccount: ProviderAccountSummary,
  status: ProviderAccountStatus.catch('active').optional(),
  concurrencyLimit: z.number(),
  inUse: z.number(),
  running: z.array(Build).default([]),
  waiting: z.array(z.object({ build: Build, position: z.number(), waitingSince: str, reason: str, estimatedStartAt: nstr })).default([]),
});
export const QueueSnapshot = z.object({ generatedAt: str, lanes: z.array(QueueLane), pendingApproval: z.number().optional(), awaitingClient: z.number().optional() });
export type QueueSnapshot = z.infer<typeof QueueSnapshot>;
